import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Row, Col } from 'antd';

import CustomModal from './CustomModal';
import OnTraccrTextInput from '../inputs/OnTraccrTextInput';
import OnTraccrButton from '../buttons/OnTraccrButton';

import colors from '../../constants/Colors';

const ACTION_LABELS = {
  archive: 'Archive',
  activate: 'Activate',
  delete: 'Delete',
};

export default function BulkActionPasswordModal({
  visible,
  onClose,
  onConfirm,
  action,
  eligibleCount = 0,
  skippedCount = 0,
  entityLabel = 'items',
  warningHeader,
  warningSubtext,
}) {
  const [text, setText] = useState();
  const actionLabel = ACTION_LABELS[action] || 'Confirm';
  const promptForPassword = eligibleCount > 1;

  const onChange = useCallback((e) => {
    setText(e?.target?.value);
  }, []);

  const onCancel = useCallback(() => {
    onClose();
    setText();
  }, [onClose]);

  const onConfirmClicked = useCallback(async () => {
    if (await onConfirm({ password: text })) {
      onCancel();
      setText();
    }
  }, [onConfirm, text, onCancel]);

  const countSubtext = useMemo(() => {
    const itemText = skippedCount === 1 ? 'item' : 'items';
    const skippedText = skippedCount > 0
      ? ` ${skippedCount} selected ${itemText} will be skipped.`
      : '';
    return `This will ${action} ${eligibleCount} ${entityLabel}.${skippedText}`;
  }, [action, eligibleCount, skippedCount, entityLabel]);

  return (
    <CustomModal
      title={`${actionLabel} ${eligibleCount} ${entityLabel}?`}
      visible={visible}
      hideCloseButton
      maskColor={colors.MODAL_MASK_DARK}
    >
      <div style={{
        fontSize: 14,
        padding: 10,
        marginBottom: 10,
      }}>
        <b>Warning:</b>
        {' '}
        {warningHeader || countSubtext}
        <br />
        <br />
        {warningSubtext}
        {warningHeader ? (
          <>
            {countSubtext}
          </>
        ) : null}
        {promptForPassword && (
          <>
            <br />
            <br />
            Please enter your password below to confirm.
          </>
        )}
      </div>
      {promptForPassword && (
        <Row style={{ margin: '20px 5px' }}>
          <OnTraccrTextInput
            password
            autoComplete="new-password"
            placeholder="Enter your password"
            onChange={onChange}
            value={text}
          />
        </Row>
      )}
      <Row justify="center" gutter={12} style={{ marginBottom: 10 }}>
        <Col>
          <OnTraccrButton
            type="cancel"
            title="Cancel"
            roundStyle
            onClick={onCancel}
          />
        </Col>
        <Col>
          <OnTraccrButton
            title={actionLabel}
            roundStyle
            disabled={promptForPassword && !text}
            onClick={onConfirmClicked}
          />
        </Col>
      </Row>
    </CustomModal>
  );
}

BulkActionPasswordModal.propTypes = {
  visible: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  action: PropTypes.oneOf(['archive', 'activate', 'delete']).isRequired,
  eligibleCount: PropTypes.number,
  skippedCount: PropTypes.number,
  entityLabel: PropTypes.string,
  warningHeader: PropTypes.string,
  warningSubtext: PropTypes.string,
};

BulkActionPasswordModal.defaultProps = {
  visible: false,
  eligibleCount: 0,
  skippedCount: 0,
  entityLabel: 'items',
  warningHeader: null,
  warningSubtext: null,
};